import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Message } from "./api/assistant";
import Link from "next/link";
import Image from "next/image";
import { parseLecturePath } from "../../helpers/blaze_qa";
import { useRouter } from "next/router";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import { BlazeSessionConfirmation } from "./FeedbackUI";
import ResourcesSection from "../../components/ResourcesSection";
import dynamic from "next/dynamic";
const Lottie = dynamic(() => import("lottie-react-web"), { ssr: false });
import searchInstructor from "../../public/assets/lottie/searching_instructor.json";
import {
  ExclamationTriangleIcon,
  AcademicCapIcon,
} from "@heroicons/react/24/solid";
import remarkGfm from "remark-gfm";
import LiveClassesContainer from "./LiveClassesContainer";
// Import the ChatLiveClassCard component at the top of the file with other imports
import ChatLiveClassCard from "./ChatLiveClassCard";
import BlazeSessionsLoginCard from "./BlazeSessionsLoginCard";
import useAuth from "../../hooks/useAuth";
import classNames from "classnames";

// Custom styles to ensure consistent text colors regardless of theme
const markdownStyles = {
  // Ensure all text has consistent color and is larger
  color: "#f1f1f1", // YouTube's off-white
  fontSize: "1rem", // Slightly larger base font size
  lineHeight: "1.5",

  // Style links with a consistent color that passes contrast checks
  a: {
    color: "#3ea6ff" as const, // YouTube's blue link color
    textDecoration: "underline" as const,
    fontWeight: "500" as const,
  },

  // Style headings with better visibility
  h1: {
    color: "#f1f1f1",
    fontWeight: "bold" as const,
    fontSize: "1.5em",
    marginTop: "0.7em",
    marginBottom: "0.5em",
  },
  h2: {
    color: "white",
    fontWeight: "bold" as const,
    fontSize: "1.2em",
    marginTop: "0.5em",
    marginBottom: "0.35em",
  },
  h3: {
    color: "white",
    fontWeight: "bold" as const,
    fontSize: "1.1em",
    marginTop: "0.5em",
    marginBottom: "0.35em",
  },
  h4: {
    color: "white",
    fontWeight: "bold" as const,
    fontSize: "1em",
    marginTop: "0.5em",
    marginBottom: "0.35em",
  },
  h5: {
    color: "white",
    fontWeight: "bold" as const,
    fontSize: "0.9em",
    marginTop: "0.5em",
    marginBottom: "0.35em",
  },
  h6: {
    color: "white",
    fontWeight: "bold" as const,
    fontSize: "0.8em",
    marginTop: "0.5em",
    marginBottom: "0.35em",
  },
  // Style code blocks
  code: {
    backgroundColor: "#2d2d2d", // Slightly lighter than YouTube's bg
    padding: "0.2em 0.4em",
    borderRadius: "3px",
    fontSize: "0.9em",
    fontFamily: "monospace",
  },
  // Style pre blocks (code blocks)
  pre: {
    backgroundColor: "#2d2d2d",
    padding: "1em",
    borderRadius: "4px",
    overflowX: "auto" as const,
    fontSize: "0.9em",
    margin: "0.7em 0",
  },
  // Style lists
  ul: {
    paddingLeft: "1.5em",
    marginTop: "0.5em",
    marginBottom: "0.5em",
  },
  ol: {
    paddingLeft: "1.5em",
    marginTop: "0.5em",
    marginBottom: "0.5em",
  },
  li: {
    marginBottom: "0.3em",
  },
  // Style blockquotes
  blockquote: {
    borderLeft: "3px solid #4b5563",
    paddingLeft: "1em",
    color: "#e2e8f0",
    fontStyle: "italic" as const,
  },
  // Style tables
  table: { color: "white", borderCollapse: "collapse" as const, width: "100%" },
  th: {
    color: "white",
    borderBottom: "1px solid #4b5563",
    padding: "0.5em",
    textAlign: "left" as const,
  },
  td: { color: "white", borderBottom: "1px solid #4b5563", padding: "0.5em" },
};

// Custom components for ReactMarkdown to ensure consistent styling
const MarkdownComponents = {
  p: (props: any) => <p style={{ color: "white" }}>{props.children}</p>,
  a: (props: any) => (
    <a
      href={props.href}
      style={markdownStyles.a}
      target="_blank"
      rel="noopener noreferrer"
    >
      {props.children}
    </a>
  ),
  h1: (props: any) => <h1 style={markdownStyles.h1}>{props.children}</h1>,
  h2: (props: any) => <h2 style={markdownStyles.h2}>{props.children}</h2>,
  h3: (props: any) => <h3 style={markdownStyles.h3}>{props.children}</h3>,
  h4: (props: any) => <h4 style={markdownStyles.h4}>{props.children}</h4>,
  h5: (props: any) => <h5 style={markdownStyles.h5}>{props.children}</h5>,
  h6: (props: any) => <h6 style={markdownStyles.h6}>{props.children}</h6>,
  code: (props: any) => {
    const { inline, className, children } = props;
    return (
      <code
        style={inline ? markdownStyles.code : undefined}
        className={className}
      >
        {children}
      </code>
    );
  },
  pre: (props: any) => <pre style={markdownStyles.pre}>{props.children}</pre>,
  ul: (props: any) => <ul style={markdownStyles.ul}>{props.children}</ul>,
  ol: (props: any) => <ol style={markdownStyles.ol}>{props.children}</ol>,
  li: (props: any) => <li style={markdownStyles.li}>{props.children}</li>,
  blockquote: (props: any) => (
    <blockquote style={markdownStyles.blockquote}>{props.children}</blockquote>
  ),
  table: (props: any) => (
    <table style={markdownStyles.table}>{props.children}</table>
  ),
  th: (props: any) => <th style={markdownStyles.th}>{props.children}</th>,
  td: (props: any) => <td style={markdownStyles.td}>{props.children}</td>,
};

interface ChatMessageProps {
  message: Message;
  isConsecutive?: boolean;
  isLastInGroup?: boolean;
  onFeedbackClick?: (messageId: string) => void;
  resourcesLoading?: boolean;
  requestCancelled?: boolean;
  retryResourceFetch?: () => void;
  selectedBuddyId?: string | null;
}

// Add this helper function at the top level of the file
const buildLectureUrl = (lecturePath: any) => {
  const baseParams = new URLSearchParams({
    subject: lecturePath.subject,
    chapter: lecturePath.chapter,
    lecture_item_id: lecturePath.lecture_item,
  });

  if (lecturePath.lecture_header_item) {
    baseParams.append(
      "lecture_header_item_id",
      lecturePath.lecture_header_item
    );
  }

  return `/auth_classroom?${baseParams.toString()}`;
};

// Helper function to get buddy information
const getBuddyInfo = (buddyId: string | null) => {
  const buddyMap = {
    explainer: {
      name: "Concept Explainer",
      icon: "📚",
      color: "bg-gradient-to-r from-cyan-500 to-blue-600",
      description: "Detailed explanations",
    },
    blaze: {
      name: "Blaze Sessions",
      icon: "👨‍🏫",
      color: "bg-gradient-to-r from-orange-500 to-red-600",
      description: "1-on-1 tutoring",
    },
    live: {
      name: "Class Finder",
      icon: "🎬",
      color: "bg-gradient-to-r from-green-500 to-emerald-700",
      description: "Live classes",
    },
    search: {
      name: "Video Lectures",
      icon: "🎥",
      color: "bg-gradient-to-r from-blue-500 to-indigo-600",
      description: "Video content",
    },
    learning: {
      name: "Study Guide",
      icon: "📝",
      color: "bg-gradient-to-r from-purple-500 to-pink-600",
      description: "Learning paths",
    },
    settings: {
      name: "Settings Helper",
      icon: "⚙️",
      color: "bg-gradient-to-r from-gray-500 to-gray-700",
      description: "Platform settings",
    },
    navigation: {
      name: "Quick Links",
      icon: "🚀",
      color: "bg-gradient-to-r from-amber-500 to-yellow-600",
      description: "Navigation help",
    },
  };

  return buddyMap[buddyId || "explainer"] || buddyMap.explainer; // Fallback to explainer instead of default
};

// Buddy Badge Component
const BuddyBadge = ({ buddyId }: { buddyId: string | null }) => {
  // Don't show badge for null, undefined, or "default" buddyId - but do show for explainer
  if (!buddyId || buddyId === "default") return null;

  const buddy = getBuddyInfo(buddyId);

  return (
    <div className="flex items-center mb-2">
      <div
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white ${buddy.color} shadow-sm`}
      >
        <span className="mr-1">{buddy.icon}</span>
        <span>{buddy.name}</span>
      </div>
    </div>
  );
};

function extractChapterIdAndSubjectCode(path: string) {
  // Split the path by '/' and remove empty segments (e.g., from leading '/')
  const segments = path.split("/").filter((segment) => segment.length > 0);

  // Find subject code
  const subjectIndex = segments.indexOf("subject");
  if (subjectIndex === -1 || subjectIndex + 1 >= segments.length) {
    throw new Error("Subject not found in path");
  }
  const subjectId = segments[subjectIndex + 1];
  const subjectCode = subjectId.split("_").pop(); // Get last part after underscore

  // Find chapter ID
  const chapterIndex = segments.indexOf("chapter");
  if (chapterIndex === -1 || chapterIndex + 1 >= segments.length) {
    throw new Error("Chapter not found in path");
  }
  const chapterId = segments[chapterIndex + 1];

  // Return both values as an object
  return {
    chapterId: chapterId,
    subjectCode: chapterId?.includes("math") ? "maths" : subjectCode,
  };
}

// Planet component for subtle decoration
const MiniPlanet = ({
  type,
  style,
}: {
  type: string;
  style: React.CSSProperties;
}) => {
  const planetSrc = `/assets/onboarding/planet_${type}.svg`;

  if (isNaN(+type)) {
    return null;
  }

  return (
    <div
      className="absolute pointer-events-none"
      style={{
        ...style,
        transition: "all 0.5s ease",
      }}
    >
      <img
        src={planetSrc}
        alt={`Planet ${type}`}
        className="w-full h-full object-contain"
      />
    </div>
  );
};

// Add this component to show a loading state for resources
const ResourcesLoadingIndicator = () => {
  return (
    <div className="mt-3 md:mt-4 pt-2 md:pt-3 border-t border-gray-700/20">
      <p className="text-sm font-medium mb-3 text-gray-300 flex items-center">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-4 w-4 mr-1.5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
        Finding Resources
      </p>
      <div className="space-y-2.5">
        {[1, 2, 3].map((item) => (
          <div
            key={item}
            className="block p-3 bg-gradient-to-r from-black/40 to-gray-900/40 backdrop-blur-sm rounded-lg border border-gray-700/20 animate-pulse"
          >
            <div className="flex items-center">
              <div className="w-16 h-9 bg-gray-800 rounded overflow-hidden mr-3 flex-shrink-0"></div>
              <div className="flex-1 min-w-0">
                <div className="h-4 bg-gray-800 rounded w-3/4 mb-2"></div>
                <div className="flex items-center mt-1.5">
                  <div className="h-3 bg-gray-800 rounded w-1/4"></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const NavigationBubble = ({
  destination,
  label,
  message,
}: {
  destination?: string;
  label?: string;
  message?: string;
}) => {
  const router = useRouter();
  const [isHovering, setIsHovering] = useState(false);

  const handleNavigation = () => {
    // Validate destination is one of our allowed routes
    let route = "/";

    if (!destination) {
      console.warn("No destination provided, defaulting to Home");
    } else if (
      destination.toLowerCase() === "blaze" ||
      destination.toLowerCase() === "/blaze" ||
      destination.toLowerCase() === "blazeaichat" ||
      destination.toLowerCase() === "ai chat"
    ) {
      route = "/blaze";
    } else if (
      destination.toLowerCase() === "classes" ||
      destination.toLowerCase() === "/classes" ||
      destination.toLowerCase() === "classroom" ||
      destination.toLowerCase() === "lectures"
    ) {
      route = "/classes";
    } else if (
      destination.toLowerCase() === "home" ||
      destination.toLowerCase() === "/" ||
      destination.toLowerCase() === "/home"
    ) {
      route = "/";
    } else {
      console.warn(
        `Unknown navigation destination: ${destination}, defaulting to Home`
      );
    }

    console.log(`Navigating to: ${route}`);
    router.push(route);
  };

  // Function to render floating cosmic elements
  const renderCosmicElements = () => {
    return (
      <>
        {/* Floating stars */}
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-white"
            initial={{
              top: `${20 + Math.random() * 60}%`,
              left: `${Math.random() * 90}%`,
              scale: 0.2 + Math.random() * 0.4,
              opacity: 0.4 + Math.random() * 0.3,
            }}
            animate={{
              y: [0, -10, 0],
              opacity: [0.4, 0.8, 0.4],
            }}
            transition={{
              duration: 2 + Math.random() * 3,
              repeat: Infinity,
              ease: "easeInOut",
              delay: Math.random() * 2,
            }}
            style={{
              width: `${2 + Math.random() * 3}px`,
              height: `${2 + Math.random() * 3}px`,
              filter: "blur(0.5px)",
            }}
          />
        ))}

        {/* Nebula effect */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-indigo-500/5 to-blue-500/10 rounded-lg"
          animate={{
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.03 }}
      className="mt-3 relative cursor-pointer"
      onClick={handleNavigation}
      onHoverStart={() => setIsHovering(true)}
      onHoverEnd={() => setIsHovering(false)}
    >
      <div className="flex flex-col p-4 rounded-lg bg-gradient-to-br from-purple-900/60 to-indigo-900/80 border border-purple-500/40 relative overflow-hidden group">
        {/* Background cosmic elements */}
        {renderCosmicElements()}

        {/* Animated pulse effect */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-r from-purple-500/0 via-purple-500/10 to-purple-500/0"
          animate={{
            x: ["-100%", "100%"],
          }}
          transition={{
            repeat: Infinity,
            repeatType: "loop",
            duration: 2,
            ease: "easeInOut",
          }}
        />

        {/* Glowing border effect on hover */}
        <motion.div
          className="absolute inset-0 rounded-lg"
          initial={{ opacity: 0 }}
          animate={{
            opacity: isHovering ? 1 : 0,
            boxShadow: isHovering
              ? "0 0 10px 1px rgba(147, 51, 234, 0.5), inset 0 0 7px 1px rgba(147, 51, 234, 0.3)"
              : "0 0 0px 0px rgba(147, 51, 234, 0)",
          }}
          transition={{ duration: 0.3 }}
        />

        <div className="flex items-center space-x-3 relative z-10">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center relative overflow-hidden">
            {/* Animated glow effect inside icon */}
            <motion.div
              className="absolute inset-0 bg-white/20"
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0, 0.3, 0],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <svg
              className="w-5 h-5 text-white relative z-10"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 5l7 7m0 0l-7 7m7-7H3"
              />
            </svg>
          </div>

          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white group-hover:bg-gradient-to-r group-hover:from-purple-200 group-hover:to-indigo-200 group-hover:text-transparent group-hover:bg-clip-text transition-all duration-300">
              {label || `Go to ${destination || "Home"}`}
            </h3>

            {/* Animated dots when hovering */}
            <div className="h-5 overflow-hidden">
              {isHovering ? (
                <motion.div
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.p
                    className="text-sm text-indigo-200"
                    animate={{
                      x: [0, 5, 0],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    Click to navigate
                    <motion.span
                      animate={{
                        opacity: [0, 1, 0],
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        repeatType: "loop",
                        ease: "easeInOut",
                        times: [0, 0.5, 1],
                      }}
                    >
                      →
                    </motion.span>
                  </motion.p>
                </motion.div>
              ) : (
                <p className="text-sm text-purple-200">Click to navigate</p>
              )}
            </div>
          </div>
        </div>

        {/* Message content */}
        {message && (
          <motion.div
            className="mt-3 pt-3 border-t border-purple-500/20 text-sm text-indigo-100/80"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <p className="relative z-10">{message}</p>

            {/* Small decorative element for the message */}
            <div className="absolute right-3 bottom-3 w-8 h-8 opacity-20">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="text-indigo-300"
              >
                <path
                  d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
};

// Get resource type icon based on type
const getResourceTypeIcon = (type: string) => {
  const lowerType = type.toLowerCase();

  if (lowerType.includes("video")) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-3 w-3 mr-1"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
      </svg>
    );
  } else if (lowerType.includes("lecture") || lowerType.includes("note")) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-3 w-3 mr-1"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
      </svg>
    );
  } else if (lowerType.includes("quiz") || lowerType.includes("practice")) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-3 w-3 mr-1"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
        <path
          fillRule="evenodd"
          d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
        />
      </svg>
    );
  } else if (lowerType.includes("article") || lowerType.includes("reading")) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-3 w-3 mr-1"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M2 5a2 2 0 012-2h8a2 2 0 012 2v10a2 2 0 002 2H4a2 2 0 01-2-2V5zm3 1h6v4H5V6zm6 6H5v2h6v-2z"
          clipRule="evenodd"
        />
        <path d="M15 7h1a2 2 0 012 2v5.5a1.5 1.5 0 01-3 0V7z" />
      </svg>
    );
  } else {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-3 w-3 mr-1"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
          clipRule="evenodd"
        />
      </svg>
    );
  }
};

// Get resource type color based on resource type
const getResourceTypeColor = (type: string): string => {
  const lowerType = type.toLowerCase();

  if (lowerType.includes("video")) {
    return "from-blue-700/40 to-blue-900/40 border-blue-500/30 text-blue-200";
  } else if (lowerType.includes("lecture") || lowerType.includes("note")) {
    return "from-green-700/40 to-green-900/40 border-green-500/30 text-green-200";
  } else if (lowerType.includes("quiz") || lowerType.includes("practice")) {
    return "from-purple-700/40 to-purple-900/40 border-purple-500/30 text-purple-200";
  } else if (lowerType.includes("article") || lowerType.includes("reading")) {
    return "from-amber-700/40 to-amber-900/40 border-amber-500/30 text-amber-200";
  } else {
    return "from-gray-700/40 to-gray-900/40 border-gray-600/30 text-gray-200";
  }
};

// Learning Path UI Component
const LearningPathUI = ({ learningPath, router }) => {
  // State to track which steps have expanded "why" sections
  const [expandedSteps, setExpandedSteps] = useState({});

  // Toggle explanation for a step
  const toggleExpand = (stepKey) => {
    setExpandedSteps((prev) => ({
      ...prev,
      [stepKey]: !prev[stepKey],
    }));
  };

  // Define section colors
  const sectionColors = {
    introduction: "blue-500",
    core_concepts: "purple-500",
    application_examples: "green-500",
    practice_exercises: "amber-500",
    review_consolidation: "indigo-500",
  };

  // Define section titles and icons
  const sectionInfo = {
    introduction: {
      title: "Introduction & Foundation",
      icon: (
        <svg
          className="h-3 w-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    core_concepts: {
      title: "Core Concepts & Methods",
      icon: (
        <svg
          className="h-3 w-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          />
        </svg>
      ),
    },
    application_examples: {
      title: "Application & Examples",
      icon: (
        <svg
          className="h-3 w-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
          />
        </svg>
      ),
    },
    practice_exercises: {
      title: "Practice & Exercises",
      icon: (
        <svg
          className="h-3 w-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
          />
        </svg>
      ),
    },
    review_consolidation: {
      title: "Review & Consolidation",
      icon: (
        <svg
          className="h-3 w-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      ),
    },
  };

  // Helper function to render a path step
  const renderPathStep = (step, index, sectionColor, isLast) => {
    const stepKey = `${sectionColor}-${step.step}-${index}`;
    const isExpanded = expandedSteps[stepKey];

    // Get subtle color classes
    const getTextColorClass = (color) => {
      if (color === "blue-500") return "text-blue-400/80";
      if (color === "purple-500") return "text-purple-400/80";
      if (color === "green-500") return "text-green-400/80";
      if (color === "amber-500") return "text-amber-400/80";
      return "text-indigo-400/80";
    };

    // Handle lecture click
    const handleLectureClick = () => {
      if (step.lecture_path && router) {
        const parsedPath = parseLecturePath(step.lecture_path);
        const url = buildLectureUrl(parsedPath);
        window.open(url, "_blank");
      }
    };

    const stepContentClasses = step.lecture_path
      ? "py-1 mb-2.5 cursor-pointer hover:bg-gray-800/30 rounded transition-colors px-1 -ml-1"
      : "py-1 mb-2.5";

    return (
      <div key={stepKey} className="pl-6 relative group">
        {/* Path line */}
        {!isLast && (
          <div
            className="absolute left-[7px] top-[14px] bottom-0 w-[1px] bg-gradient-to-b from-gray-700 to-gray-800"
            style={{
              backgroundImage: `linear-gradient(to bottom, rgb(55,65,81), rgb(31,41,55,0.5))`,
            }}
          />
        )}

        {/* Step dot */}
        <div
          className={`absolute left-[5px] top-[7px] w-[5px] h-[5px] rounded-full bg-gray-500 z-10 ring-1 ring-black transition-all duration-300 group-hover:scale-110 group-hover:bg-gray-300`}
        />

        <div
          className={stepContentClasses}
          onClick={step.lecture_path ? handleLectureClick : undefined}
        >
          <div className="flex items-center mb-1">
            <div className={`text-xs font-medium text-white mr-1.5 opacity-90`}>
              {index + 1}.
            </div>
            <div className="text-xs font-medium text-white flex-1 truncate opacity-90">
              {step.lecture_title}
            </div>
            <div
              className={`ml-2 px-1.5 py-0.5 text-[10px] rounded uppercase tracking-wide border-b ${getTextColorClass(
                sectionColor
              )} bg-black/20 opacity-80`}
            >
              {step.lecture_type}
            </div>
          </div>

          <p className="text-[11px] text-gray-400 line-clamp-1">
            {step.instruction}
          </p>

          <div className="flex justify-between items-center mt-1.5">
            <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent triggering parent onClick
                toggleExpand(stepKey);
              }}
              className={`text-[10px] flex items-center text-gray-500 hover:text-gray-300 transition-colors`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-2.5 w-2.5 mr-1 transition-transform ${
                  isExpanded ? "rotate-180" : "rotate-0"
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
              {isExpanded ? "Hide" : "Why?"}
            </button>

            {step.lecture_path && (
              <button
                onClick={(e) => {
                  e.stopPropagation(); // Prevent triggering parent onClick
                  handleLectureClick();
                }}
                className={`text-[10px] flex items-center font-medium text-gray-400 hover:text-white transition-colors`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-2.5 w-2.5 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
                Open
              </button>
            )}
          </div>

          {isExpanded && (
            <div className="mt-1.5 bg-gray-800/30 backdrop-blur-sm rounded px-2 py-1.5 border-l-2 border-gray-700">
              <p className="text-[11px] text-gray-400 italic">
                <span className="text-gray-300 font-medium">Why:</span>{" "}
                {step.why}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Function to get the subtle accent color for section headers
  const getSectionAccentColor = (key) => {
    switch (key) {
      case "introduction":
        return "from-blue-900/20 to-transparent border-l-blue-600/30";
      case "core_concepts":
        return "from-purple-900/20 to-transparent border-l-purple-600/30";
      case "application_examples":
        return "from-green-900/20 to-transparent border-l-green-600/30";
      case "practice_exercises":
        return "from-amber-900/20 to-transparent border-l-amber-600/30";
      case "review_consolidation":
        return "from-indigo-900/20 to-transparent border-l-indigo-600/30";
      default:
        return "from-gray-800/40 to-transparent border-l-gray-700";
    }
  };

  // Function to get the icon color class
  const getIconColorClass = (key) => {
    switch (key) {
      case "introduction":
        return "text-blue-400";
      case "core_concepts":
        return "text-purple-400";
      case "application_examples":
        return "text-green-400";
      case "practice_exercises":
        return "text-amber-400";
      case "review_consolidation":
        return "text-indigo-400";
      default:
        return "text-gray-400";
    }
  };

  return (
    <div className="mt-2 space-y-4">
      {/* Path Header */}
      <div className="rounded-md bg-gradient-to-r from-gray-900/80 to-gray-800/50 backdrop-blur-sm p-2.5 border border-gray-700/50">
        <div className="flex items-center mb-1.5">
          <div className="p-1.5 bg-gray-800/80 rounded-full mr-2.5">
            <svg
              className="h-4 w-4 text-blue-400"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-medium text-white">
              Personalized Learning Path
            </h3>
            <p className="text-xs text-gray-400 mt-0.5">
              A structured approach to master this topic step-by-step
            </p>
          </div>
        </div>
      </div>

      {/* Define the correct order of sections */}
      {[
        "introduction",
        "core_concepts",
        "application_examples",
        "practice_exercises",
        "review_consolidation",
      ].map((sectionKey) => {
        // Skip empty sections
        if (
          !learningPath[sectionKey] ||
          learningPath[sectionKey].length === 0
        ) {
          return null;
        }

        // Get the section color based on the key
        const sectionColor = sectionColors[sectionKey];
        const steps = learningPath[sectionKey];

        return (
          <div key={sectionKey} className="relative">
            {/* Section Header */}
            <div
              className={`pl-2.5 py-1.5 mb-1 rounded-l-md bg-gradient-to-r ${getSectionAccentColor(
                sectionKey
              )} border-l-2`}
            >
              <div className="flex items-center">
                <div className={`${getIconColorClass(sectionKey)} mr-2`}>
                  {sectionInfo[sectionKey].icon}
                </div>
                <h4 className="text-xs font-medium text-gray-200">
                  {sectionInfo[sectionKey].title}
                </h4>
              </div>
            </div>

            {/* Section Steps */}
            <div className="mt-2">
              {steps.map((step, index) =>
                renderPathStep(
                  step,
                  index,
                  sectionColor,
                  index === steps.length - 1
                )
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Add new component for suggested follow-ups
const SuggestedFollowUps = ({ followUps, onClick }) => {
  if (!followUps || followUps.length === 0) return null;

  return (
    <div className="mt-4 pt-3 border-t border-gray-700/20">
      <motion.p
        className="text-sm font-medium mb-3 flex items-center"
        initial={{ opacity: 0.8 }}
        animate={{ opacity: [0.8, 1, 0.8] }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <span className="bg-gradient-to-r from-purple-400 via-indigo-300 to-blue-400 text-transparent bg-clip-text">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1.5 inline-block text-indigo-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
            />
          </svg>
          Follow-up Questions
        </span>
      </motion.p>
      <div className="flex flex-wrap gap-2 mt-2">
        {followUps.map((question, index) => (
          <motion.button
            key={index}
            onClick={() => onClick(question)}
            className="relative px-3 py-1.5 text-xs bg-gradient-to-r from-gray-800/80 to-gray-900/80 hover:from-indigo-900/50 hover:to-purple-900/50 active:from-purple-900/70 active:to-indigo-900/70 text-gray-100 hover:text-white rounded-md border border-gray-700/30 hover:border-indigo-500/40 transition-all duration-200 text-left flex items-center shadow-sm hover:shadow-lg hover:shadow-indigo-500/10 group"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97, transition: { duration: 0.1 } }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.2,
              delay: index * 0.05,
            }}
            title="Click to use this question"
            data-tooltip="Click to use this question"
            aria-label={`Use follow-up question: ${question}`}
          >
            <div className="mr-2 flex-shrink-0 opacity-60 group-hover:opacity-90 transition-opacity">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <ReactMarkdown
              className="text-sm md:text-sm leading-relaxed markdown-explicit-colors [&_p]:!mb-0"
              remarkPlugins={[remarkMath, remarkGfm]}
              rehypePlugins={[rehypeKatex]}
              components={MarkdownComponents}
            >
              {processMarkdownForLatex(question)}
            </ReactMarkdown>

            {/* Subtle hint that appears on hover */}
            <motion.div
              className="absolute -top-8 left-0 bg-black/80 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap"
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 0 }}
              whileHover={{ opacity: 1, y: 0 }}
            >
              Click to use this question
            </motion.div>
          </motion.button>
        ))}
      </div>
    </div>
  );
};

// Add new component for suggested lectures
const SuggestedLecturesUI = ({
  suggestedLectures,
  router,
  getYouTubeVideoId,
}) => {
  useEffect(() => {
    console.log("suggestedLectures", suggestedLectures);
  }, [suggestedLectures]);

  if (!suggestedLectures || suggestedLectures.length === 0) return null;

  const handleLectureClick = (lecturePath) => {
    if (router && lecturePath) {
      router.push(buildLectureUrl(parseLecturePath(lecturePath)));
    }
  };

  return (
    <div className="mt-4 space-y-4">
      <motion.p
        className="text-sm font-medium mb-3 flex items-center"
        initial={{ opacity: 0.8 }}
        animate={{ opacity: [0.8, 1, 0.8] }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <span className="bg-gradient-to-r from-blue-400 via-indigo-300 to-purple-400 text-transparent bg-clip-text">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1.5 inline-block text-indigo-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
          Suggested Lectures
        </span>
      </motion.p>
      <div className="space-y-2.5">
        {suggestedLectures.map((lecture, index) => {
          const resourceType = lecture.lecture_type || "concept";

          return (
            <div
              key={index}
              className="block p-3 bg-gradient-to-r from-black/40 to-gray-900/40 backdrop-blur-sm rounded-lg border border-gray-700/20 hover:border-indigo-500/40 transition-all duration-300 transform hover:scale-[1.01] hover:shadow-lg hover:shadow-indigo-500/10 cursor-pointer"
              onClick={() => handleLectureClick(lecture.lecture_path)}
              style={{
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Add subtle glow effect */}
              <div className="absolute inset-0 bg-indigo-500/5 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>

              <div className="flex items-center">
                {lecture.youtube_url ? (
                  <div className="w-16 h-9 bg-gray-900/50 rounded overflow-hidden mr-3 flex-shrink-0 border border-gray-700/30 shadow-sm">
                    <Image
                      src={`https://img.youtube.com/vi/${
                        getYouTubeVideoId(lecture.youtube_url) || "default"
                      }/mqdefault.jpg`}
                      alt=""
                      className="w-full h-full object-cover opacity-90 hover:opacity-100 transition-opacity"
                      width={640}
                      height={360}
                    />
                  </div>
                ) : (
                  <div
                    className={`w-10 h-10 rounded-full overflow-hidden mr-3 flex-shrink-0 border border-gray-700/30 shadow-sm flex items-center justify-center bg-gradient-to-r ${getResourceTypeColor(
                      resourceType
                    )}`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-white"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium line-clamp-1 text-white">
                    {lecture.lecture_title}
                  </p>
                  <p className="text-xs text-gray-400 mt-1 line-clamp-1">
                    {lecture.description}
                  </p>
                  <div className="flex items-center mt-1.5 flex-wrap">
                    <span
                      className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-gradient-to-r ${getResourceTypeColor(
                        resourceType
                      )} border`}
                    >
                      {getResourceTypeIcon(resourceType)}
                      {resourceType
                        .split("-")
                        .map(
                          (word) => word.charAt(0).toUpperCase() + word.slice(1)
                        )
                        .join(" ")}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Function to process markdown for latex
function processMarkdownForLatex(input: string) {
  let output = input;

  // Convert inline math \( ... \) to $ ... $
  output = output.replace(/\\\((.*?)\\\)/gs, (match, p1) => `$${p1}$`);

  // Convert display math \[ ... \] to $$ ... $$
  output = output.replace(/\\\[(.*?)\\\]/gs, (match, p1) => `$$${p1}$$`);

  // Remove newlines within inline math $ ... $, preserving newlines outside
  output = output.replace(/(\$(?:[^$]|\n)*?\$)/gs, (match) => {
    return match.replace(/\n+/g, " ");
  });

  // Handle newlines within display math $$ ... $$, converting to aligned environment
  output = output.replace(/(\$\$(?:[^$]|\n)*?\$\$)/gs, (match) => {
    // Extract content between $$...$$, excluding the delimiters
    const innerContent = match.slice(2, -2).trim();
    // If there are newlines, wrap in aligned environment
    if (innerContent.includes("\n")) {
      const lines = innerContent
        .split(/\n+/)
        .filter((line) => line.trim())
        .join(" \\\\\n");
      return `$$\n\\begin{aligned}\n${lines}\n\\end{aligned}\n$$`;
    }
    return match;
  });

  return output;
}

// BlazeSessionsList component to display Blaze sessions
const BlazeSessionsList = ({
  sessions,
  message,
}: {
  sessions: Array<{
    id: string;
    title: string;
    subject: string;
    topic: string;
    student_id: string;
    tutor_id: string | null;
    tutor_name: string;
    status: string;
    created_at: string;
    completed_at: string | null;
    grade: string;
    score: number;
    detail_url: string;
    subject_color_gradient?: string[] | null;
    student_name?: string | null;
    student_profile_pic?: string | null;
    skill?: string | null;
  }>;
  message: Message;
}) => {
  const { user } = useAuth();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [pendingRefresh, setPendingRefresh] = useState(false);
  const router = useRouter();

  // Check if user is logged in
  useEffect(() => {
    setIsLoggedIn(!!user && (user as any).uid !== "guest-user");
  }, [user]);

  // Check if this is a login request message
  const isLoginRequestMessage =
    message.text?.includes("sign in") ||
    message.text?.includes("login") ||
    message.metadata?.response_type === "clarification_needed" ||
    message.metadata?.answer?.includes("sign in") ||
    message.metadata?.answer?.includes("login");

  // Handle successful login - allow time to fetch the sessions data
  const handleLoginSuccess = () => {
    // setPendingRefresh(true);
    // // Reload the page after a short delay to refresh the sessions
    // setTimeout(() => {
    //   router.reload();
    // }, 2000);
  };

  // If there are no sessions AND this appears to be a login request
  if (
    (!sessions || sessions.length === 0) &&
    isLoginRequestMessage &&
    !isLoggedIn
  ) {
    return (
      <BlazeSessionsLoginCard
        message={message}
        onLoginSuccess={handleLoginSuccess}
      />
    );
  }

  // Show a loading message when waiting for sessions after login
  if (pendingRefresh) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center py-8"
      >
        <div className="flex items-center justify-center space-x-2">
          <div className="w-4 h-4 rounded-full bg-indigo-600 animate-pulse"></div>
          <div className="w-4 h-4 rounded-full bg-indigo-500 animate-pulse delay-100"></div>
          <div className="w-4 h-4 rounded-full bg-indigo-400 animate-pulse delay-200"></div>
        </div>
        <p className="text-white mt-4">Fetching your Blaze sessions...</p>
      </motion.div>
    );
  }

  // Normal case - user is logged in but no sessions found
  if (!sessions || sessions.length === 0) {
    return <p className="text-sm text-gray-300">No Blaze sessions found.</p>;
  }

  // Function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(date);
  };

  // Function to extract subject name from skill
  const getSubjectName = (skill: string | null | undefined) => {
    if (!skill) return "General";

    const splitted = skill.split("_");
    if (splitted.length > 0) {
      return splitted.length === 3
        ? splitted.includes("_math")
          ? `${splitted[0]} ${splitted[1]}th`
          : splitted[2]
        : splitted.slice(3).join(" ");
    }
    return "General";
  };

  // Format subject name for display
  const formatSubjectName = (
    subject: string,
    skill: string | null | undefined
  ) => {
    if (subject !== "General") {
      return subject === "maths" ? "Mathematics" : subject;
    } else {
      return getSubjectName(skill);
    }
  };

  return (
    <div className="space-y-2 max-w-full">
      <ReactMarkdown
        className="text-sm md:text-sm leading-relaxed markdown-explicit-colors mb-4"
        remarkPlugins={[remarkMath, remarkGfm]}
        rehypePlugins={[rehypeKatex]}
        components={MarkdownComponents}
      >
        {processMarkdownForLatex(
          message.metadata?.detected_language === "hinglish" ||
            message.metadata?.detected_language === "hindi"
            ? message.metadata?.hinglish_answer || message.text
            : message.metadata?.english_answer || message.text
        )}
      </ReactMarkdown>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {sessions.map((session) => {
          const gradient = session.subject_color_gradient || [
            "#4f46e5",
            "#6366f1",
          ];

          // Extract subject name from skill field
          const getSubjectNameFromSkill = (skill) => {
            if (!skill) return "General";

            const parts = skill.split("_");
            if (parts.length >= 4) {
              // Format: class_7_science_natural_resources
              return parts[2].charAt(0).toUpperCase() + parts[2].slice(1);
            }
            return "General";
          };

          const subjectName = getSubjectNameFromSkill(session.skill);
          // Handle both status and session_status fields since the interface expects status
          const isOutstanding =
            (session as any).session_status?.toLowerCase() === "outstanding" ||
            session.status?.toLowerCase() === "outstanding";

          return (
            <motion.div
              key={session.id}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="blaze-session-card"
              style={{
                height: "120px",
                borderRadius: "12px",
                overflow: "hidden",
                position: "relative",
                transition: "transform 0.2s, box-shadow 0.2s",
                boxShadow: "0 0 8px 0 rgba(0, 0, 0, 0.2)",
              }}
            >
              <Link
                href={`/blaze/session/${session.id}`}
                className="block h-full w-full text-inherit no-underline"
              >
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    borderRadius: "12px",
                    zIndex: 0,
                    background: `rgba(17, 24, 39, 0.8)`,
                    backdropFilter: "blur(8px)",
                  }}
                />
                <div
                  style={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    height: "100%",
                    border: `1px solid ${gradient[0]}60`,
                    borderRadius: "12px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: "4px",
                      height: "100%",
                      flexShrink: 0,
                      backgroundColor: gradient[0],
                      boxShadow: `4px 0 14px 3px ${gradient[1]}80`,
                    }}
                  />

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                      padding: "12px",
                      paddingLeft: "14px",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          overflow: "hidden",
                          maxWidth: "calc(100% - 80px)",
                        }}
                      >
                        <h3
                          style={{
                            fontSize: "0.95rem",
                            fontWeight: 500,
                            color: "white",
                            margin: 0,
                            padding: 0,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {session.topic || "Untitled Topic"}
                        </h3>
                        <div
                          style={{
                            fontSize: "0.7rem",
                            color: "rgba(255, 255, 255, 0.5)",
                            marginTop: "2px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            textTransform: "capitalize",
                          }}
                        >
                          {subjectName}
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: "0.65rem",
                          padding: "2px 8px",
                          borderRadius: "9999px",
                          color: "white",
                          textTransform: "capitalize",
                          letterSpacing: "0.02em",
                          fontWeight: 500,
                          background: `linear-gradient(to right, ${gradient[0]}, ${gradient[1]})`,
                        }}
                      >
                        {/* Use session_status if available, fall back to status */}
                        {(session as any).session_status ||
                          session.status ||
                          "Pending"}
                      </div>
                    </div>

                    <div
                      style={{
                        background: "rgba(128, 128, 128, 0.2)",
                        height: "1px",
                        width: "100%",
                        margin: "8px 0",
                      }}
                    ></div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {!isOutstanding ? (
                          <div
                            style={{
                              width: "28px",
                              height: "28px",
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "12px",
                              color: "white",
                              marginRight: "10px",
                              flexShrink: 0,
                              background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`,
                            }}
                          >
                            {/* Use instructor_name if available, fall back to tutor_name */}
                            {(session as any).instructor_name?.charAt(0) ||
                              session.tutor_name?.charAt(0) ||
                              "?"}
                          </div>
                        ) : (
                          <div
                            style={{
                              width: "30px",
                              height: "30px",
                              marginRight: "10px",
                              flexShrink: 0,
                            }}
                            className="instructor-search"
                          >
                            <Lottie
                              options={{
                                animationData: searchInstructor,
                                loop: true,
                              }}
                            />
                          </div>
                        )}

                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            minWidth: 0,
                          }}
                        >
                          <div
                            style={{
                              fontSize: "0.8rem",
                              color: "rgba(255, 255, 255, 0.9)",
                              fontWeight: 500,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "100px",
                            }}
                          >
                            {isOutstanding
                              ? "Searching..."
                              : (session as any).instructor_name ||
                                session.tutor_name ||
                                "Unassigned"}
                          </div>
                          <div
                            style={{
                              fontSize: "0.7rem",
                              color: "rgba(255, 255, 255, 0.6)",
                              textTransform: "capitalize",
                              marginTop: "2px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {subjectName} Expert
                          </div>
                        </div>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginLeft: "auto",
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{
                            width: "16px",
                            height: "16px",
                            color: "rgba(255, 255, 255, 0.5)",
                            transition: "transform 0.2s",
                          }}
                          className="blaze-session-arrow"
                        >
                          <path d="M9 18l6-6-6-6" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          );
        })}
      </div>

      <style jsx global>{`
        @keyframes pulse {
          0% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
          }
          70% {
            transform: scale(1);
            box-shadow: 0 0 0 5px rgba(16, 185, 129, 0);
          }
          100% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
          }
        }

        .blaze-session-card:hover {
          transform: translateY(-2px) !important;
          box-shadow: 0 0 12px 2px rgba(99, 102, 241, 0.4) !important;
        }

        .blaze-session-card:hover .blaze-session-arrow {
          transform: translateX(2px) !important;
          color: rgba(255, 255, 255, 0.7) !important;
        }

        .instructor-search {
          display: flex;
          align-items: center;
          justify-content: center;
        }
      `}</style>
    </div>
  );
};

export default function ChatMessage({
  message,
  isConsecutive = false,
  isLastInGroup = true,
  onFeedbackClick,
  resourcesLoading = false,
  requestCancelled = false,
  retryResourceFetch,
  selectedBuddyId,
}: ChatMessageProps) {
  const router = useRouter();
  const [showCopyButton, setShowCopyButton] = useState(false);
  const [copied, setCopied] = useState(false);
  const isUser = message.sender === "user";

  // Check if the message is an error message
  const isErrorMessage =
    message.metadata?.error_type || message.metadata?.original_error;

  // Add handler for follow-up questions
  const handleFollowUpClick = (question: string) => {
    if (typeof window !== "undefined") {
      // Dispatch a custom event with the selected question
      // This follows React patterns by using a well-defined event communication channel
      const event = new CustomEvent("assistantFollowUpQuestion", {
        detail: {
          question,
          timestamp: Date.now(),
        },
      });
      window.dispatchEvent(event);

      // Log for debugging
      console.log("Selected follow-up question:", question);
    }
  };

  // Reset copied state after 2 seconds
  useEffect(() => {
    if (copied) {
      const timeout = setTimeout(() => {
        setCopied(false);
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [copied]);

  // Handle copy code
  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopied(true);
  };

  // Determine if we should show a planet (only show for some messages to avoid clutter)
  const shouldShowPlanet =
    message.id && parseInt(message.id.slice(-2), 16) % 5 === 0;
  const planetType = message.id
    ? (parseInt(message.id.slice(-1), 16) % 8) + 1
    : 1;

  const formatTime = (timestamp: any) => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const getYouTubeVideoId = (url: string): string | null => {
    if (!url) return null;
    if (url.includes("youtu.be/")) {
      const id = url.split("youtu.be/")[1]?.split(/[?&]/)[0];
      return id || null;
    }
    if (url.includes("youtube.com/watch")) {
      const urlParams = new URLSearchParams(url.split("?")[1]);
      return urlParams.get("v");
    }
    if (url.includes("youtube.com/embed/")) {
      return url.split("youtube.com/embed/")[1]?.split(/[?&]/)[0] || null;
    }
    return null;
  };

  const getBubbleClasses = () => {
    if (message.sender === "user") {
      return classNames(
        "py-3 px-4 rounded-2xl", // Slightly more padding for better text spacing
        isConsecutive ? "rounded-tl-md" : "",
        // Higher contrast background with YouTube-like approach
        "bg-[#263342] text-[#f1f1f1]", // YouTube-inspired dark blue with off-white text
        "shadow-sm"
      );
    } else {
      // For assistant messages
      return classNames(
        "py-3 px-4 rounded-2xl", // Consistent padding increase
        isConsecutive ? "rounded-tr-md" : "",
        // YouTube-like assistant message background with better contrast
        "bg-[#222222] text-[#f1f1f1]", // Slightly lighter than main bg but with high contrast text
        "shadow-sm"
      );
    }
  };

  // Function to handle chapter click and redirect to classroom
  const handleChapterClick = () => {
    if (
      message.metadata?.detected_chapter &&
      message.metadata?.detected_subject
    ) {
      try {
        // Get the path from the first lecture resource if available
        let chapterPath = "";

        if (
          message.metadata?.recommendations &&
          message.metadata.recommendations.length > 0
        ) {
          // Use the path from the first recommendation
          chapterPath = message.metadata.recommendations[0].path;
        } else {
          // Fallback to constructing a path if no recommendations are available
          const subjectLower = message.metadata.detected_subject.toLowerCase();
          const chapterLower = message.metadata.detected_chapter
            .toLowerCase()
            .replace(/\s+/g, "");

          chapterPath = `/category/class_9_learn_${subjectLower}/subject/class_9_learn_${subjectLower}_${chapterLower}/chapter/class_9_learn_${subjectLower}_${chapterLower}`;
        }

        console.log("Using chapter path:", chapterPath);
        const extractedData = extractChapterIdAndSubjectCode(chapterPath);
        console.log("extractedData - ", extractedData);

        // Check if user is logged in
        const auth = require("../../firebase-config").auth;
        const isLoggedIn = auth && auth.currentUser !== null;

        if (extractedData && extractedData.subjectCode) {
          if (isLoggedIn) {
            // Redirect to auth_classroom for logged-in users
            router.push({
              pathname: "/auth_classroom",
              query: {
                subject: extractedData.subjectCode,
                chapter: extractedData.chapterId,
              },
            });
          } else {
            // Redirect to classroom for guest users
            router.push({
              pathname: "/classroom",
              query: {
                subject: extractedData.subjectCode,
                chapter: extractedData.chapterId,
              },
            });
          }
        } else {
          // Fallback if extraction fails
          console.error(
            "Failed to extract chapter data from path:",
            chapterPath
          );
        }
      } catch (error) {
        console.error("Error navigating to chapter:", error);
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`${
        isConsecutive
          ? "!mt-1"
          : message.messageType === "cancellation"
          ? "!mt-3 !mb-3"
          : "!mt-4"
      } max-w-full relative ${
        message.sender === "assistant" &&
        (message.metadata?.isStreaming ||
          message.metadata?.interimMessage ||
          message.metadata?.isSearching)
          ? "mb-24"
          : "" // Add bottom margin when typing indicator is shown
      }`}
    >
      <div
        className={`flex flex-col ${
          message.sender === "user" ? "items-end" : "items-start"
        }`}
      >
        {!isConsecutive &&
          message.sender === "assistant" &&
          message.messageType !== "cancellation" && (
            <div className="flex items-center mb-1 justify-start">
              <div className="w-7 h-7 rounded-full flex items-center justify-center bg-gradient-to-r from-gray-700 to-gray-900 text-white overflow-hidden">
                <img
                  src="/assets/onboarding/gulu-dp.svg"
                  alt="AI Assistant"
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="text-xs text-gray-300 ml-1">PuStack AI</span>
            </div>
          )}

        <div
          className={`${
            message.messageType === "cancellation" ? "p-0 ml-1" : "p-3"
          } ${getBubbleClasses()} ${
            isConsecutive ? "max-w-[95%]" : "max-w-[95%]"
          }`}
          style={{
            boxShadow:
              message.messageType === "cancellation"
                ? "none"
                : message.sender === "user"
                ? "0 1px 2px rgba(30, 86, 49, 0.2)"
                : isErrorMessage
                ? "0 2px 10px rgba(220, 38, 38, 0.2), inset 0 1px 0 0 rgba(254, 202, 202, 0.05)"
                : message.metadata?.response_type === "academic_out_of_scope"
                ? "0 2px 10px rgba(180, 83, 9, 0.2), inset 0 1px 0 0 rgba(255, 230, 170, 0.05)"
                : "0 2px 10px rgba(76, 29, 149, 0.15)",
          }}
        >
          {message.sender === "assistant" ? (
            <div>
              {/* Show buddy badge for assistant messages */}
              <BuddyBadge
                buddyId={
                  (message.metadata?.selectedBuddyId || selectedBuddyId) ?? null
                }
              />

              {message.messageType === "blazeSessionConfirmation" &&
              message.metadata?.sessionId &&
              message.metadata?.topic ? (
                <BlazeSessionConfirmation
                  sessionId={message.metadata.sessionId}
                  topic={message.metadata.topic}
                />
              ) : message.metadata?.isNavigation ? (
                <NavigationBubble
                  destination={message.metadata.navigationTarget}
                  label={message.metadata.navigationLabel}
                  message={message.metadata.navigationMessage}
                />
              ) : message.messageType === "cancellation" ? (
                <motion.div
                  initial={{ opacity: 0, y: 5 }}
                  animate={{
                    opacity: 1,
                    y: [0, -1, 0],
                  }}
                  transition={{
                    y: {
                      repeat: Infinity,
                      duration: 3,
                      ease: "easeInOut",
                    },
                    opacity: { duration: 0.3 },
                  }}
                  whileHover={{ scale: 1.02 }}
                  className="relative inline-flex items-center px-3 py-1.5 rounded-md bg-gradient-to-r from-red-900/70 to-gray-900/80 backdrop-blur-sm border-l-2 border border-red-600/50 overflow-hidden group"
                  style={{
                    boxShadow:
                      "0 1px 6px rgba(239, 68, 68, 0.2), inset 0 1px 0 0 rgba(255, 255, 255, 0.05)",
                    maxWidth: "260px",
                  }}
                >
                  {/* Pulsing background */}
                  <motion.div
                    className="absolute inset-0 bg-red-600/5"
                    animate={{ opacity: [0.3, 0.5, 0.3] }}
                    transition={{
                      repeat: Infinity,
                      duration: 2,
                      ease: "easeInOut",
                    }}
                  />

                  {/* Error icon with warning triangle */}
                  <div className="relative flex-shrink-0 flex items-center justify-center mr-2.5">
                    <svg
                      className="w-3.5 h-3.5 text-red-500"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>

                  <span className="text-xs font-medium text-red-200">
                    This request was cancelled by user
                  </span>
                </motion.div>
              ) : isErrorMessage ? (
                <div className="prose prose-sm max-w-none">
                  {/* Error message indicator */}
                  <div className="mb-3 px-2 py-1.5 bg-red-900/20 rounded-md border border-red-700/30 flex items-center relative overflow-hidden">
                    {/* Subtle animated highlight */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-red-500/0 via-red-500/5 to-red-500/0"
                      animate={{
                        x: ["0%", "100%", "0%"],
                      }}
                      transition={{
                        repeat: Infinity,
                        duration: 5,
                        ease: "easeInOut",
                      }}
                    />
                    <svg
                      className="h-4 w-4 text-red-500 mr-1.5 flex-shrink-0 relative z-10"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span className="text-xs text-red-200 font-medium relative z-10">
                      {message.metadata?.error_type === "http_error"
                        ? "Connection error detected"
                        : message.metadata?.error_type === "bad_request"
                        ? "Bad request error detected"
                        : "An error occurred"}
                    </span>
                  </div>

                  <ReactMarkdown
                    className="text-sm md:text-sm leading-relaxed markdown-explicit-colors"
                    remarkPlugins={[remarkMath, remarkGfm]}
                    rehypePlugins={[rehypeKatex]}
                    components={MarkdownComponents}
                  >
                    {processMarkdownForLatex(
                      message.metadata?.detected_language === "hinglish" ||
                        message.metadata?.detected_language === "hindi"
                        ? message.metadata?.hinglish_answer ||
                            message.metadata?.answer ||
                            message.text
                        : message.metadata?.english_answer ||
                            message.metadata?.answer ||
                            message.text
                    )}
                  </ReactMarkdown>

                  {message.metadata?.original_error && (
                    <details className="mt-3 text-xs text-gray-400">
                      <summary className="cursor-pointer hover:text-gray-300 transition-colors duration-200">
                        View error details
                      </summary>
                      <div className="mt-2 p-2 bg-black/30 rounded border border-gray-700/50 whitespace-pre-wrap break-words">
                        {message.metadata.original_error}
                      </div>
                    </details>
                  )}
                </div>
              ) : (
                <div className="prose prose-sm max-w-none">
                  {message.metadata?.response_type ===
                    "academic_out_of_scope" && (
                    <div className="mb-2 px-2 py-1.5 bg-amber-900/20 rounded-md border border-amber-700/30 flex items-center relative overflow-hidden">
                      {/* Subtle animated highlight */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-amber-500/0 via-amber-500/5 to-amber-500/0"
                        animate={{
                          x: ["0%", "100%", "0%"],
                        }}
                        transition={{
                          repeat: Infinity,
                          duration: 5,
                          ease: "easeInOut",
                        }}
                      />
                      <svg
                        className="h-4 w-4 text-amber-500 mr-1.5 flex-shrink-0 relative z-10"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      <span className="text-xs text-amber-200 font-medium relative z-10">
                        {message.metadata?.detected_grade
                          ? message.metadata.detected_grade ===
                            "Beyond Class 10"
                            ? "This topic is from higher classes (11-12) and beyond NCERT syllabus for Class 7-10"
                            : "This topic is beyond the scope of Class 7-10 NCERT syllabus"
                          : "This topic is beyond the scope of Class 7-10 NCERT syllabus"}
                      </span>
                    </div>
                  )}

                  {/* Streaming indicator - show when message is still streaming */}
                  {/* Remove this since we now show TypingIndicator outside the bubble */}

                  {/* Interim message indicator - show when we have a searching message */}
                  {message.metadata?.isSearching &&
                    !message.metadata?.streamComplete && (
                      <div
                        className="mb-2 px-3 py-2.5 bg-gradient-to-br from-indigo-900/30 to-blue-900/20 rounded-lg border border-indigo-700/30 flex items-center relative overflow-hidden shadow-lg"
                        ref={(el) => {
                          if (el && message.metadata?.isSearching) {
                            // Scroll into view with smooth behavior when this element appears
                            setTimeout(() => {
                              el.scrollIntoView({
                                behavior: "smooth",
                                block: "center",
                              });
                            }, 100);
                          }
                        }}
                      >
                        {/* Background shimmer effect */}
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-indigo-500/0 via-indigo-500/15 to-blue-500/0"
                          animate={{
                            x: ["-100%", "100%"],
                          }}
                          transition={{
                            repeat: Infinity,
                            repeatType: "loop",
                            duration: 2,
                            ease: "easeInOut",
                          }}
                        />

                        {/* Pulsing circle behind the icon */}
                        <div className="relative mr-2.5 flex-shrink-0">
                          <motion.div
                            className="absolute inset-0 rounded-full bg-indigo-500/30"
                            animate={{
                              scale: [1, 1.5, 1],
                              opacity: [0.3, 0.1, 0.3],
                            }}
                            transition={{
                              repeat: Infinity,
                              duration: 2,
                              ease: "easeInOut",
                            }}
                          />
                          <svg
                            className="h-5 w-5 text-indigo-300 relative z-10"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        </div>

                        {/* Message text with gradient */}
                        <div className="flex flex-col flex-1">
                          <span className="text-sm bg-gradient-to-r from-indigo-200 to-blue-100 text-transparent bg-clip-text font-medium relative z-10">
                            Searching...
                          </span>
                          <span className="text-xs text-indigo-300/80 relative z-10 mt-0.5">
                            {message.text}
                          </span>
                        </div>

                        {/* Animated dots */}
                        <motion.div
                          className="flex items-center ml-2 space-x-1"
                          initial={{ opacity: 0.7 }}
                          animate={{ opacity: [0.7, 1, 0.7] }}
                          transition={{
                            repeat: Infinity,
                            duration: 1.5,
                            ease: "easeInOut",
                          }}
                        >
                          <motion.span
                            className="w-1.5 h-1.5 bg-indigo-400 rounded-full"
                            animate={{ y: [0, -4, 0] }}
                            transition={{
                              repeat: Infinity,
                              duration: 1,
                              delay: 0,
                              ease: "easeInOut",
                            }}
                          />
                          <motion.span
                            className="w-1.5 h-1.5 bg-indigo-300 rounded-full"
                            animate={{ y: [0, -4, 0] }}
                            transition={{
                              repeat: Infinity,
                              duration: 1,
                              delay: 0.2,
                              ease: "easeInOut",
                            }}
                          />
                          <motion.span
                            className="w-1.5 h-1.5 bg-blue-400 rounded-full"
                            animate={{ y: [0, -4, 0] }}
                            transition={{
                              repeat: Infinity,
                              duration: 1,
                              delay: 0.4,
                              ease: "easeInOut",
                            }}
                          />
                        </motion.div>
                      </div>
                    )}

                  {/* Learning Path UI for learning path response type */}
                  {message.metadata?.streamComplete &&
                    ((message.metadata?.response_type === "learning_path" ||
                      message.response_type === "learning_path") &&
                    (message.metadata?.learning_path ||
                      message.learning_path) ? (
                      <LearningPathUI
                        learningPath={
                          message.metadata?.learning_path ||
                          message.learning_path
                        }
                        router={router}
                      />
                    ) : (message.metadata?.response_type ===
                        "live_classes_schedule" ||
                        message.response_type === "live_classes_schedule") &&
                      (message.metadata?.live_classes ||
                        message.live_classes) ? (
                      <>
                        <ReactMarkdown
                          className="text-sm md:text-sm leading-relaxed markdown-explicit-colors mb-3"
                          remarkPlugins={[remarkMath, remarkGfm]}
                          rehypePlugins={[rehypeKatex]}
                          components={MarkdownComponents}
                        >
                          {processMarkdownForLatex(
                            message.metadata?.detected_language ===
                              "hinglish" ||
                              message.metadata?.detected_language === "hindi"
                              ? message.metadata?.hinglish_answer ||
                                  message.text
                              : message.metadata?.english_answer || message.text
                          )}
                        </ReactMarkdown>

                        <LiveClassesContainer
                          classes={
                            message.metadata?.live_classes ||
                            message.live_classes ||
                            []
                          }
                        />

                        {/* Add search parameters information - moved after results */}
                        {(message.metadata?.live_class_filters ||
                          message.metadata?.search_parameters ||
                          message.metadata?.start_time_iso ||
                          message.metadata?.end_time_iso ||
                          message.metadata?.subject_filter ||
                          message.metadata?.grade_filter) && (
                          <div className="mt-4 p-3 bg-gradient-to-r from-indigo-900/30 to-blue-900/20 rounded-lg border border-indigo-700/30">
                            <p className="text-xs font-medium text-indigo-200 mb-2 flex items-center">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3 mr-1.5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                                />
                              </svg>
                              What I searched for you
                            </p>
                            <div className="text-xs text-blue-100 mb-2 opacity-80">
                              Here's how I found these classes for you:
                            </div>
                            <div className="grid grid-cols-1 gap-1.5 text-xs">
                              {/* Time Period */}
                              {(message.metadata?.start_time_iso ||
                                message.metadata?.end_time_iso) && (
                                <div className="flex items-start">
                                  <span className="text-gray-300 w-16 flex-shrink-0 font-medium">
                                    📅 Period:
                                  </span>
                                  <div className="text-blue-200 flex-1">
                                    {message.metadata?.start_time_iso &&
                                    message.metadata?.end_time_iso ? (
                                      (() => {
                                        const startDate = new Date(
                                          message.metadata.start_time_iso
                                        );
                                        const endDate = new Date(
                                          message.metadata.end_time_iso
                                        );
                                        const now = new Date();

                                        // Work with UTC dates to avoid timezone conversion issues
                                        const startUTC = new Date(
                                          Date.UTC(
                                            startDate.getUTCFullYear(),
                                            startDate.getUTCMonth(),
                                            startDate.getUTCDate()
                                          )
                                        );
                                        const endUTC = new Date(
                                          Date.UTC(
                                            endDate.getUTCFullYear(),
                                            endDate.getUTCMonth(),
                                            endDate.getUTCDate()
                                          )
                                        );
                                        const todayUTC = new Date(
                                          Date.UTC(
                                            now.getUTCFullYear(),
                                            now.getUTCMonth(),
                                            now.getUTCDate()
                                          )
                                        );
                                        const tomorrowUTC = new Date(todayUTC);
                                        tomorrowUTC.setUTCDate(
                                          todayUTC.getUTCDate() + 1
                                        );
                                        const yesterdayUTC = new Date(todayUTC);
                                        yesterdayUTC.setUTCDate(
                                          todayUTC.getUTCDate() - 1
                                        );

                                        // Check if it's the same day in UTC
                                        const isSameDay =
                                          startUTC.getTime() ===
                                          endUTC.getTime();

                                        if (isSameDay) {
                                          // Single day - show user-friendly labels
                                          let dayLabel = "";
                                          let timeDescription = "";

                                          if (
                                            startUTC.getTime() ===
                                            todayUTC.getTime()
                                          ) {
                                            dayLabel = "Today";
                                          } else if (
                                            startUTC.getTime() ===
                                            tomorrowUTC.getTime()
                                          ) {
                                            dayLabel = "Tomorrow";
                                          } else if (
                                            startUTC.getTime() ===
                                            yesterdayUTC.getTime()
                                          ) {
                                            dayLabel = "Yesterday";
                                          } else {
                                            // Format the date using the original startDate for proper local display
                                            dayLabel =
                                              startDate.toLocaleDateString(
                                                "en-IN",
                                                {
                                                  day: "numeric",
                                                  month: "short",
                                                  year:
                                                    startDate.getFullYear() !==
                                                    now.getFullYear()
                                                      ? "numeric"
                                                      : undefined,
                                                }
                                              );
                                          }

                                          // Add context based on current time
                                          const isStartPast = startDate < now;
                                          const isEndFuture = endDate > now;

                                          if (
                                            startUTC.getTime() ===
                                            todayUTC.getTime()
                                          ) {
                                            if (isStartPast && isEndFuture) {
                                              timeDescription =
                                                " (includes past, present and upcoming classes)";
                                            } else if (isStartPast) {
                                              timeDescription =
                                                " (past classes)";
                                            } else {
                                              timeDescription =
                                                " (upcoming classes)";
                                            }
                                          } else if (
                                            startUTC.getTime() ===
                                            yesterdayUTC.getTime()
                                          ) {
                                            timeDescription = " (past classes)";
                                          } else if (
                                            startUTC.getTime() ===
                                            tomorrowUTC.getTime()
                                          ) {
                                            timeDescription =
                                              " (upcoming classes)";
                                          }

                                          return (
                                            <span>
                                              {dayLabel}
                                              <span className="text-blue-300 italic">
                                                {timeDescription}
                                              </span>
                                            </span>
                                          );
                                        } else {
                                          // Multi-day range
                                          const isStartPast = startDate < now;
                                          const isEndFuture = endDate > now;

                                          let timeDescription = "";
                                          if (isStartPast && isEndFuture) {
                                            timeDescription =
                                              " (includes past, present and upcoming classes)";
                                          } else if (isStartPast) {
                                            timeDescription = " (past classes)";
                                          } else {
                                            timeDescription =
                                              " (upcoming classes)";
                                          }

                                          return (
                                            <span>
                                              {startDate.toLocaleDateString(
                                                "en-IN",
                                                {
                                                  day: "numeric",
                                                  month: "short",
                                                  year:
                                                    startDate.getFullYear() !==
                                                    now.getFullYear()
                                                      ? "numeric"
                                                      : undefined,
                                                }
                                              )}{" "}
                                              to{" "}
                                              {endDate.toLocaleDateString(
                                                "en-IN",
                                                {
                                                  day: "numeric",
                                                  month: "short",
                                                  year:
                                                    endDate.getFullYear() !==
                                                    now.getFullYear()
                                                      ? "numeric"
                                                      : undefined,
                                                }
                                              )}
                                              <span className="text-blue-300 italic">
                                                {timeDescription}
                                              </span>
                                            </span>
                                          );
                                        }
                                      })()
                                    ) : message.metadata?.start_time_iso ? (
                                      <span>
                                        From{" "}
                                        {new Date(
                                          message.metadata.start_time_iso
                                        ).toLocaleDateString("en-IN", {
                                          day: "numeric",
                                          month: "short",
                                          year:
                                            new Date(
                                              message.metadata.start_time_iso
                                            ).getFullYear() !==
                                            new Date().getFullYear()
                                              ? "numeric"
                                              : undefined,
                                        })}{" "}
                                        onwards
                                      </span>
                                    ) : (
                                      <span>
                                        Until{" "}
                                        {new Date(
                                          message.metadata.end_time_iso
                                        ).toLocaleDateString("en-IN", {
                                          day: "numeric",
                                          month: "short",
                                          year:
                                            new Date(
                                              message.metadata.end_time_iso
                                            ).getFullYear() !==
                                            new Date().getFullYear()
                                              ? "numeric"
                                              : undefined,
                                        })}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Subject Filter */}
                              {message.metadata?.subject_filter && (
                                <div className="flex items-center">
                                  <span className="text-gray-300 w-16 flex-shrink-0 font-medium">
                                    📚 Subject:
                                  </span>
                                  <span className="text-green-200 font-medium">
                                    {message.metadata.subject_filter} classes
                                    only
                                  </span>
                                </div>
                              )}

                              {/* Grade Filter */}
                              {message.metadata?.grade_filter && (
                                <div className="flex items-center">
                                  <span className="text-gray-300 w-16 flex-shrink-0 font-medium">
                                    🎓 Grade:
                                  </span>
                                  <span className="text-purple-200 font-medium">
                                    {message.metadata.grade_filter
                                      .toString()
                                      .startsWith("class_")
                                      ? `${message.metadata.grade_filter.replace(
                                          "class_",
                                          "Class "
                                        )}`
                                      : `Class ${message.metadata.grade_filter}`}{" "}
                                    students
                                  </span>
                                </div>
                              )}

                              {/* Status Filter */}
                              {message.metadata?.status_filter && (
                                <div className="flex items-center">
                                  <span className="text-gray-300 w-16 flex-shrink-0 font-medium">
                                    ⏱️ Type:
                                  </span>
                                  <span
                                    className={`font-medium capitalize ${
                                      message.metadata.status_filter === "live"
                                        ? "text-red-200"
                                        : message.metadata.status_filter ===
                                          "upcoming"
                                        ? "text-yellow-200"
                                        : message.metadata.status_filter ===
                                          "completed"
                                        ? "text-gray-200"
                                        : "text-gray-200"
                                    }`}
                                  >
                                    {message.metadata.status_filter === "live"
                                      ? "🔴 Live classes (happening now)"
                                      : message.metadata.status_filter ===
                                        "upcoming"
                                      ? "⏰ Upcoming classes only"
                                      : message.metadata.status_filter ===
                                        "completed"
                                      ? "✅ Past/completed classes"
                                      : message.metadata.status_filter}
                                  </span>
                                </div>
                              )}

                              {/* Topic/Query */}
                              {message.metadata?.query_text && (
                                <div className="flex items-start">
                                  <span className="text-gray-300 w-16 flex-shrink-0 font-medium">
                                    🔍 Topic:
                                  </span>
                                  <span className="text-cyan-200 italic">
                                    Classes about "{message.metadata.query_text}
                                    "
                                  </span>
                                </div>
                              )}

                              {/* Results Limit */}
                              {message.metadata?.max_results && (
                                <div className="flex items-center">
                                  <span className="text-gray-300 w-16 flex-shrink-0 font-medium">
                                    📊 Showing:
                                  </span>
                                  <span className="text-amber-200">
                                    Up to {message.metadata.max_results} best
                                    matches
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Helpful tip */}
                            <div className="mt-2 pt-2 border-t border-indigo-700/20">
                              <p className="text-xs text-indigo-200 opacity-75 italic">
                                💡 Tip: You can ask me to search for different
                                subjects, grades, or time periods anytime!
                              </p>
                            </div>
                          </div>
                        )}
                      </>
                    ) : message.metadata?.response_type ===
                        "blaze_sessions_list" ||
                      message.response_type === "blaze_sessions_list" ? (
                      <BlazeSessionsList
                        sessions={message.metadata?.blaze_sessions || []}
                        message={message}
                      />
                    ) : message.metadata?.show_feedback_ui ||
                      message.show_feedback_ui ||
                      String(message.metadata?.response_type).includes(
                        "blaze_session_request"
                      ) ||
                      String(message.response_type).includes(
                        "blaze_session_request"
                      ) ? (
                      // When show_feedback_ui is true or response_type is blaze_session_request,
                      // the FeedbackUI component will be shown by the parent component,
                      // so we just display the message text
                      <ReactMarkdown
                        className="text-sm md:text-sm leading-relaxed markdown-explicit-colors mb-3"
                        remarkPlugins={[remarkMath, remarkGfm]}
                        rehypePlugins={[rehypeKatex]}
                        components={MarkdownComponents}
                      >
                        {processMarkdownForLatex(
                          message.metadata?.detected_language === "hinglish" ||
                            message.metadata?.detected_language === "hindi"
                            ? message.metadata?.hinglish_answer ||
                                message.metadata?.answer ||
                                message.text
                            : message.metadata?.english_answer ||
                                message.metadata?.answer ||
                                message.text
                        )}
                      </ReactMarkdown>
                    ) : (message.metadata?.response_type ===
                        "clarification_needed" ||
                        message.metadata?.response_type ===
                          "authentication_required") &&
                      (message.metadata?.answer?.includes("sign in") ||
                        message.metadata?.answer?.includes("login") ||
                        message.metadata?.english_answer?.includes("sign in") ||
                        message.metadata?.english_answer?.includes("login")) ? (
                      <BlazeSessionsLoginCard
                        message={message}
                        onLoginSuccess={() => {
                          // Refresh the page after successful login
                          // if (typeof window !== "undefined") {
                          //   setTimeout(() => {
                          //     window.location.reload();
                          //   }, 2000);
                          // }
                        }}
                      />
                    ) : message.metadata?.response_type ===
                        "buddy_recommendation" ||
                      message.response_type === "buddy_recommendation" ? (
                      // Instead of custom UI, show the recommendation message and trigger feedback UI
                      <div>
                        <ReactMarkdown
                          className="text-sm text-gray-200 mb-3 leading-relaxed"
                          remarkPlugins={[remarkMath, remarkGfm]}
                          rehypePlugins={[rehypeKatex]}
                          components={MarkdownComponents}
                        >
                          {message.metadata?.buddy_message ||
                            message.buddy_message ||
                            message.text}
                        </ReactMarkdown>

                        {/* Auto-trigger feedback UI for buddy recommendations */}
                        {(() => {
                          // Set the buddy recommendation data on window for FeedbackUI to access
                          if (typeof window !== "undefined") {
                            (window as any).lastBuddyRecommendation = {
                              response_type: "buddy_recommendation",
                              recommended_buddy:
                                message.metadata?.recommended_buddy ||
                                message.recommended_buddy,
                              explanation:
                                message.metadata?.buddy_explanation ||
                                message.buddy_explanation,
                              suggestion:
                                message.metadata?.buddy_suggestion ||
                                message.buddy_suggestion,
                              switch_prompt:
                                message.metadata?.switch_prompt ||
                                message.switch_prompt,
                            };
                          }

                          // Set the message as needing feedback to show the buddy switcher
                          setTimeout(() => {
                            if (onFeedbackClick && message.id) {
                              onFeedbackClick(message.id);
                            }
                          }, 100);
                          return null;
                        })()}
                      </div>
                    ) : message.metadata?.response_type ===
                      "academic_within_scope" ? (
                      <ReactMarkdown
                        className="text-sm md:text-sm leading-relaxed markdown-explicit-colors mb-3"
                        remarkPlugins={[remarkMath, remarkGfm]}
                        rehypePlugins={[rehypeKatex]}
                        components={MarkdownComponents}
                      >
                        {processMarkdownForLatex(
                          message.metadata?.detected_language === "hinglish" ||
                            message.metadata?.detected_language === "hindi"
                            ? message.metadata?.hinglish_answer ||
                                message.metadata?.answer ||
                                message.text
                            : message.metadata?.english_answer ||
                                message.metadata?.answer ||
                                message.text
                        )}
                      </ReactMarkdown>
                    ) : (
                      // Add debugging for fallback case
                      ((() => {
                        console.log(
                          "🔍 FALLBACK DEBUG - Message falling through to default:",
                          {
                            messageResponseType: message.response_type,
                            metadataResponseType:
                              message.metadata?.response_type,
                            messageText:
                              message.text?.substring(0, 100) + "...",
                            hasJsonInText:
                              message.text?.includes('{"response_type"'),
                          }
                        );
                        return null;
                      })(),
                      (
                        <div className="flex items-center space-x-2">
                          <svg
                            className="h-4 w-4 text-indigo-400 mr-1 flex-shrink-0 animate-pulse"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                          <ReactMarkdown
                            className="text-sm md:text-sm leading-relaxed markdown-explicit-colors mb-3"
                            remarkPlugins={[remarkMath, remarkGfm]}
                            rehypePlugins={[rehypeKatex]}
                            components={MarkdownComponents}
                          >
                            {processMarkdownForLatex(
                              message.metadata?.detected_language ===
                                "hinglish" ||
                                message.metadata?.detected_language === "hindi"
                                ? message.metadata?.hinglish_answer ||
                                    message.metadata?.answer ||
                                    message.text
                                : message.metadata?.english_answer ||
                                    message.metadata?.answer ||
                                    message.text
                            )}
                          </ReactMarkdown>
                        </div>
                      ))
                    ))}

                  {/* Render lecture suggestions based on both message.response_type and message.metadata.response_type */}
                  {(message.response_type === "lecture_suggestion" ||
                    message.metadata?.response_type === "lecture_suggestion") &&
                    (message.suggested_lectures ||
                      message.metadata?.suggested_lectures) && (
                      <SuggestedLecturesUI
                        suggestedLectures={
                          message.suggested_lectures ||
                          message.metadata?.suggested_lectures
                        }
                        router={router}
                        getYouTubeVideoId={getYouTubeVideoId}
                      />
                    )}

                  {/* Add suggestion for out-of-scope topics */}
                  {message.metadata?.response_type ===
                    "academic_out_of_scope" && (
                    <div className="mt-4 pt-3 border-t border-amber-700/20">
                      <p className="text-xs text-gray-300 italic">
                        <span className="text-amber-200">
                          Looking for age-appropriate topics?
                        </span>{" "}
                        Try exploring topics from your curriculum such as basic
                        mathematics, science concepts, or language studies for
                        grades 6-10. You can ask questions by clicking on the
                        suggested follow-ups below.
                      </p>
                    </div>
                  )}

                  {/* Show resources section if resources exist in the message and response type is academic_within_scope */}
                  {/* REMOVED: ResourcesSection component
                  {message.resources &&
                    message.resources.length > 0 &&
                    message.metadata?.response_type ===
                      "academic_within_scope" && (
                      <ResourcesSection
                        resources={message.resources}
                        retryResourceFetch={retryResourceFetch || (() => {})}
                      />
                    )}
                  */}

                  {/* Show resources loading indicator when resources are being loaded and response type is academic_within_scope */}
                  {resourcesLoading &&
                    !requestCancelled &&
                    message.messageType !== "blazeSessionConfirmation" && (
                      <ResourcesLoadingIndicator />
                    )}

                  {/* Show cancelled resources UI when resources were cancelled and response type is academic_within_scope */}
                  {requestCancelled &&
                    message.sender === "assistant" &&
                    message.messageType !== "cancellation" &&
                    message.messageType !== "blazeSessionConfirmation" &&
                    message.metadata?.response_type ===
                      "academic_within_scope" && (
                      <div className="mt-3 p-3 bg-gradient-to-r from-red-900/20 to-black/30 backdrop-blur-sm rounded-lg border border-red-800/30">
                        <p className="text-sm font-medium mb-2 text-gray-200 flex items-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 mr-1.5 text-red-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          Resources couldn't be loaded
                        </p>
                        <p className="text-xs text-gray-300 mb-3">
                          Resources request was cancelled. Would you like to try
                          again?
                        </p>
                        <button
                          onClick={retryResourceFetch}
                          className="text-xs text-gray-100 hover:text-white bg-red-700/50 hover:bg-red-700/70 px-3 py-1.5 rounded-md transition-colors flex items-center shadow-sm border border-red-700/30"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3 w-3 mr-1.5"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                          </svg>
                          Retry Loading Resources
                        </button>
                      </div>
                    )}

                  {/* Show recommendations section if recommendations exist */}
                  {message.metadata?.recommendations &&
                    message.metadata.recommendations.length > 0 && (
                      <div className="mt-3 md:mt-4 pt-2 md:pt-3 border-t border-gray-700/20">
                        <motion.p
                          className="text-sm font-medium mb-3 flex items-center"
                          initial={{ opacity: 0.8 }}
                          animate={{ opacity: [0.8, 1, 0.8] }}
                          transition={{
                            duration: 4,
                            repeat: Infinity,
                            ease: "easeInOut",
                          }}
                        >
                          <span className="bg-gradient-to-r from-blue-400 via-indigo-300 to-purple-400 text-transparent bg-clip-text">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4 mr-1.5 inline-block text-indigo-400"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                              />
                            </svg>
                            Recommended Resources
                          </span>
                        </motion.p>
                        <div className="space-y-2.5">
                          {message.metadata.recommendations
                            .slice(0, 3)
                            .map((rec, index) => (
                              <Link
                                key={index}
                                href={buildLectureUrl(
                                  parseLecturePath(rec.path)
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block p-3 bg-gradient-to-r from-black/40 to-gray-900/40 backdrop-blur-sm rounded-lg border border-gray-700/20 hover:border-indigo-500/40 transition-all duration-300 transform hover:scale-[1.01] hover:shadow-lg hover:shadow-indigo-500/10"
                                style={{
                                  position: "relative",
                                  overflow: "hidden",
                                }}
                              >
                                {/* Add subtle glow effect */}
                                <div className="absolute inset-0 bg-indigo-500/5 opacity-0 hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>

                                <div className="flex items-center">
                                  {rec.youtube_url && (
                                    <div className="w-16 h-9 bg-gray-900/50 rounded overflow-hidden mr-3 flex-shrink-0 border border-gray-700/30 shadow-sm">
                                      <Image
                                        src={`https://img.youtube.com/vi/${
                                          getYouTubeVideoId(rec.youtube_url) ||
                                          "default"
                                        }/mqdefault.jpg`}
                                        alt=""
                                        className="w-full h-full object-cover opacity-90 hover:opacity-100 transition-opacity"
                                        width={640}
                                        height={360}
                                      />
                                    </div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium line-clamp-1 text-white">
                                      {rec.title}
                                    </p>
                                    <div className="flex items-center mt-1.5 flex-wrap">
                                      <span
                                        className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full bg-gradient-to-r ${getResourceTypeColor(
                                          rec.type
                                        )} border`}
                                      >
                                        {getResourceTypeIcon(rec.type)}
                                        {rec.type
                                          .split("-")
                                          .map(
                                            (word) =>
                                              word.charAt(0).toUpperCase() +
                                              word.slice(1)
                                          )
                                          .join(" ")}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </Link>
                            ))}
                        </div>

                        {/* Display live classes within the Recommended Resources section */}
                        {(() => {
                          // Get live classes from either source
                          const liveClasses =
                            message.live_classes ||
                            message.metadata?.live_classes;

                          // Only render if we have classes
                          return liveClasses && liveClasses.length > 0 ? (
                            <div className="mt-4 border-t border-gray-700/20 pt-4">
                              <p className="text-sm font-medium mb-3 text-gray-200 flex items-center">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4 mr-1.5 text-purple-400"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                                  />
                                </svg>
                                Related Live Classes
                              </p>

                              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                {liveClasses.map((classData) => (
                                  <ChatLiveClassCard
                                    key={classData.id}
                                    classData={classData}
                                  />
                                ))}
                              </div>
                            </div>
                          ) : null;
                        })()}

                        {/* Display Blaze sessions within the Recommended Resources section */}
                        {(() => {
                          // Get Blaze sessions from either source
                          const blazeSessions =
                            message.blaze_sessions ||
                            message.metadata?.blaze_sessions;

                          // Only render if we have sessions
                          return blazeSessions && blazeSessions.length > 0 ? (
                            <div className="mt-4 border-t border-gray-700/20 pt-4">
                              <p className="text-sm font-medium mb-3 text-gray-200 flex items-center">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-4 w-4 mr-1.5 text-orange-400"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                  />
                                </svg>
                                Your Related Blaze Sessions
                              </p>

                              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {blazeSessions.slice(0, 4).map((session) => {
                                  const gradient =
                                    session.subject_color_gradient || [
                                      "#4f46e5",
                                      "#6366f1",
                                    ];

                                  // Extract subject name from skill field
                                  const getSubjectNameFromSkill = (skill) => {
                                    if (!skill) return "General";

                                    const parts = skill.split("_");
                                    if (parts.length >= 4) {
                                      // Format: class_7_science_natural_resources
                                      return (
                                        parts[2].charAt(0).toUpperCase() +
                                        parts[2].slice(1)
                                      );
                                    }
                                    return "General";
                                  };

                                  const subjectName = getSubjectNameFromSkill(
                                    session.skill
                                  );
                                  // Handle both status and session_status fields since the interface expects status
                                  const isOutstanding =
                                    (
                                      session as any
                                    ).session_status?.toLowerCase() ===
                                      "outstanding" ||
                                    session.status?.toLowerCase() ===
                                      "outstanding";

                                  return (
                                    <motion.div
                                      key={session.id}
                                      initial={{ opacity: 0, y: 5 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      transition={{ duration: 0.2 }}
                                      className="blaze-session-card"
                                      style={{
                                        height: "120px",
                                        borderRadius: "12px",
                                        overflow: "hidden",
                                        position: "relative",
                                        transition:
                                          "transform 0.2s, box-shadow 0.2s",
                                        boxShadow:
                                          "0 0 8px 0 rgba(0, 0, 0, 0.2)",
                                      }}
                                    >
                                      <Link
                                        href={`/blaze/session/${session.id}`}
                                        className="block h-full w-full text-inherit no-underline"
                                      >
                                        <div
                                          style={{
                                            position: "absolute",
                                            inset: 0,
                                            borderRadius: "12px",
                                            zIndex: 0,
                                            background: `rgba(17, 24, 39, 0.8)`,
                                            backdropFilter: "blur(8px)",
                                          }}
                                        />
                                        <div
                                          style={{
                                            position: "relative",
                                            zIndex: 1,
                                            display: "flex",
                                            height: "100%",
                                            border: `1px solid ${gradient[0]}60`,
                                            borderRadius: "12px",
                                            overflow: "hidden",
                                          }}
                                        >
                                          <div
                                            style={{
                                              width: "4px",
                                              height: "100%",
                                              flexShrink: 0,
                                              backgroundColor: gradient[0],
                                              boxShadow: `4px 0 14px 3px ${gradient[1]}80`,
                                            }}
                                          />

                                          <div
                                            style={{
                                              display: "flex",
                                              flexDirection: "column",
                                              flex: 1,
                                              padding: "12px",
                                              paddingLeft: "14px",
                                              justifyContent: "space-between",
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  overflow: "hidden",
                                                  maxWidth: "calc(100% - 10px)",
                                                }}
                                              >
                                                <h3
                                                  style={{
                                                    fontSize: "0.95rem",
                                                    fontWeight: 500,
                                                    color: "white",
                                                    margin: 0,
                                                    padding: 0,
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                  }}
                                                >
                                                  {session.topic ||
                                                    "Untitled Topic"}
                                                </h3>
                                                <div
                                                  style={{
                                                    fontSize: "0.7rem",
                                                    color:
                                                      "rgba(255, 255, 255, 0.5)",
                                                    marginTop: "2px",
                                                    whiteSpace: "nowrap",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    textTransform: "capitalize",
                                                  }}
                                                >
                                                  {subjectName}
                                                </div>
                                              </div>
                                            </div>

                                            <div
                                              style={{
                                                background:
                                                  "rgba(128, 128, 128, 0.2)",
                                                height: "1px",
                                                width: "100%",
                                                margin: "8px 0",
                                              }}
                                            ></div>

                                            <div
                                              style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                }}
                                              >
                                                {!isOutstanding ? (
                                                  <div
                                                    style={{
                                                      width: "28px",
                                                      height: "28px",
                                                      borderRadius: "50%",
                                                      display: "flex",
                                                      alignItems: "center",
                                                      justifyContent: "center",
                                                      fontSize: "12px",
                                                      color: "white",
                                                      marginRight: "10px",
                                                      flexShrink: 0,
                                                      background: `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`,
                                                    }}
                                                  >
                                                    {/* Use instructor_name if available, fall back to tutor_name */}
                                                    {(
                                                      session as any
                                                    ).instructor_name?.charAt(
                                                      0
                                                    ) ||
                                                      session.tutor_name?.charAt(
                                                        0
                                                      ) ||
                                                      "?"}
                                                  </div>
                                                ) : (
                                                  <div
                                                    style={{
                                                      width: "30px",
                                                      height: "30px",
                                                      marginRight: "10px",
                                                      flexShrink: 0,
                                                    }}
                                                    className="instructor-search"
                                                  >
                                                    <Lottie
                                                      options={{
                                                        animationData:
                                                          searchInstructor,
                                                        loop: true,
                                                      }}
                                                    />
                                                  </div>
                                                )}

                                                <div
                                                  style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    minWidth: 0,
                                                  }}
                                                >
                                                  <div
                                                    style={{
                                                      fontSize: "0.8rem",
                                                      color:
                                                        "rgba(255, 255, 255, 0.9)",
                                                      fontWeight: 500,
                                                      whiteSpace: "nowrap",
                                                      overflow: "hidden",
                                                      textOverflow: "ellipsis",
                                                      maxWidth: "100px",
                                                    }}
                                                  >
                                                    {isOutstanding
                                                      ? "Searching..."
                                                      : (session as any)
                                                          .instructor_name ||
                                                        session.tutor_name ||
                                                        "Unassigned"}
                                                  </div>
                                                  <div
                                                    style={{
                                                      fontSize: "0.7rem",
                                                      color:
                                                        "rgba(255, 255, 255, 0.6)",
                                                      textTransform:
                                                        "capitalize",
                                                      marginTop: "2px",
                                                      whiteSpace: "nowrap",
                                                      overflow: "hidden",
                                                      textOverflow: "ellipsis",
                                                    }}
                                                  >
                                                    {subjectName} Expert
                                                  </div>
                                                </div>
                                              </div>

                                              <div
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  marginLeft: "auto",
                                                }}
                                              >
                                                <svg
                                                  xmlns="http://www.w3.org/2000/svg"
                                                  viewBox="0 0 24 24"
                                                  fill="none"
                                                  stroke="currentColor"
                                                  strokeWidth="2"
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  style={{
                                                    width: "16px",
                                                    height: "16px",
                                                    color:
                                                      "rgba(255, 255, 255, 0.5)",
                                                    transition:
                                                      "transform 0.2s",
                                                  }}
                                                  className="blaze-session-arrow"
                                                >
                                                  <path d="M9 18l6-6-6-6" />
                                                </svg>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </Link>
                                    </motion.div>
                                  );
                                })}
                              </div>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    )}

                  {/* Show suggested follow-ups if available */}
                  {(message.suggested_follow_ups ||
                    message.metadata?.suggested_follow_ups) && (
                    <SuggestedFollowUps
                      followUps={
                        message.suggested_follow_ups ||
                        message.metadata?.suggested_follow_ups
                      }
                      onClick={handleFollowUpClick}
                    />
                  )}

                  <div className="mt-3 flex flex-col space-y-2">
                    {(message.metadata?.detected_subject ||
                      message.metadata?.detected_chapter) &&
                      message.messageType !== "cancellation" && (
                        <div className="flex items-center text-xs text-gray-300/70">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-3.5 w-3.5 mr-1.5 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>

                          <span className="truncate">
                            {message.metadata?.detected_subject && (
                              <span className="text-gray-200">
                                {message.metadata.detected_subject}
                              </span>
                            )}

                            {message.metadata?.detected_subject &&
                              message.metadata?.detected_chapter && (
                                <span className="mx-1 text-gray-300/50">•</span>
                              )}

                            {message.metadata?.detected_chapter && (
                              <button
                                onClick={handleChapterClick}
                                className="text-gray-300 font-medium hover:text-gray-200 hover:underline focus:outline-none transition-colors"
                              >
                                {message.metadata.detected_chapter}
                              </button>
                            )}
                          </span>
                        </div>
                      )}
                  </div>
                </div>
              )}

              {/* Show feedback buttons for assistant messages */}
              {!isUser &&
                isLastInGroup &&
                onFeedbackClick &&
                message.messageType !== "cancellation" && (
                  <div className="flex justify-end mt-2 space-x-2">
                    <button
                      onClick={() => onFeedbackClick(message.id)}
                      className="text-xs text-gray-400 hover:text-white bg-gray-700/50 hover:bg-gray-700 px-2 py-1 rounded-full transition-colors flex items-center"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-3 w-3 mr-1"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Feedback
                    </button>
                  </div>
                )}

              {/* Add subtle planet for some user messages */}
              {isUser && shouldShowPlanet && (
                <MiniPlanet
                  type={(planetType + 3).toString()}
                  style={{
                    top: "-10px",
                    right: "-15px",
                    width: "20px",
                    height: "20px",
                    opacity: 0.4,
                    zIndex: 1,
                  }}
                />
              )}
            </div>
          ) : (
            <div className="prose prose-sm max-w-none">
              <ReactMarkdown
                className="text-sm md:text-sm leading-relaxed markdown-explicit-colors"
                remarkPlugins={[remarkMath, remarkGfm]}
                rehypePlugins={[rehypeKatex]}
                components={MarkdownComponents}
              >
                {processMarkdownForLatex(message.text)}
              </ReactMarkdown>
            </div>
          )}

          {isLastInGroup && message.messageType !== "cancellation" && (
            <div
              className={`text-[10px] ${
                message.sender === "user"
                  ? "text-white/70 text-right mt-1"
                  : "text-gray-400 mt-1"
              }`}
            >
              {formatTime(message.timestamp)}
            </div>
          )}
        </div>
      </div>

      {/* Add subtle planet for assistant/user messages */}
      {shouldShowPlanet && message.messageType !== "cancellation" && (
        <MiniPlanet
          type={isUser ? (planetType + 3).toString() : planetType.toString()}
          style={{
            top: "-10px",
            [isUser ? "right" : "left"]: "-15px",
            width: "20px",
            height: "20px",
            opacity: 0.4,
            zIndex: 1,
          }}
        />
      )}

      {/* Add subtle cosmic dust effect for assistant messages */}
      {!isUser && message.messageType !== "cancellation" && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-10 -right-10 w-20 h-20 rounded-full bg-indigo-500/5 blur-xl"></div>
          <div className="absolute -bottom-10 -left-10 w-20 h-20 rounded-full bg-purple-500/5 blur-xl"></div>
        </div>
      )}

      {/* Add feedback buttons for assistant messages */}
      {!isUser &&
        isLastInGroup &&
        onFeedbackClick &&
        message.messageType !== "cancellation" && (
          <div className="flex justify-end mt-2 space-x-2">
            <button
              onClick={() => onFeedbackClick(message.id)}
              className="text-xs text-gray-400 hover:text-white bg-gray-700/50 hover:bg-gray-700 px-2 py-1 rounded-full transition-colors flex items-center"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-3 w-3 mr-1"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                  clipRule="evenodd"
                />
              </svg>
              Feedback
            </button>
          </div>
        )}
    </motion.div>
  );
}
